import {
  BarChart3,
  Building2,
  Clapperboard,
  Compass,
  Cpu,
  Drama,
  Eye,
  Film,
  Flame,
  Globe,
  GraduationCap,
  Gauge,
  Headphones,
  Image as ImageIcon,
  MapPin,
  Megaphone,
  MessagesSquare,
  MessageSquareText,
  Newspaper,
  Radar,
  Radio,
} from "lucide-react";

import { getBrand, type Brand } from "@/shared/brand";
import { ROLES } from "@/shared/lib/rbac";

import type { CabinPresentation, CapabilityCard } from "./types";

/**
 * 推广舱过渡软链(1012 老板拍板):推广舱暂时只接「点卡免密进 GEO 管理系统」。
 * 后端 catalog 里 ai_promotion 仍是 coming_soon(不卖、不进套餐门),前端在品牌开了 geoAdmin
 * (直营默认开,渠道默认关)时把它当已开卡;进不进得去由 GEO 页自己的接口判(未开通给提示)。
 * ★推广舱真做时:catalog 转 active,删掉这里和侧栏的软链分支。
 */
export const PROMOTION_SOFTLINK_CAP = "ai_promotion";
export const PROMOTION_SOFTLINK_ROUTE = "/ai-promotion";

export function isPromotionSoftLinked(brand: Pick<Brand, "modules">): boolean {
  return !!brand.modules.geoAdmin;
}

/** 卡墙用:软链生效时把推广舱的 coming_soon 卡翻成已开(其余卡原样)。 */
export function withPromotionSoftLink(
  card: CapabilityCard,
  brand: Pick<Brand, "modules">
): CapabilityCard {
  return card.capability === PROMOTION_SOFTLINK_CAP &&
    card.status === "coming_soon" &&
    isPromotionSoftLinked(brand)
    ? { ...card, status: "active" }
    : card;
}

/**
 * 各舱的前端画法单源(presentation overlay)。key = capability = moduleKey(不变)。
 *
 * ★内容 4 舱 render=dept:交给既有 `WorkbenchDeptCard`(积分标走 creditRates 单源、hover 真样图,
 * 已建已测),这里只标 hub / icon,route / cost / chips / desc 全从 `DEPT_ENTRIES` 拿 —— 不重写、不漂。
 * ★其余 11 舱 render=capability:走轻量 `CapabilityCard`(icon + 状态标 + 简介 + 来源标 + CTA)。
 *   已开服务舱(声量/客服/陪练)带真 route;融入中 / 即将占位无 route(不假点)。
 */
export const CABIN_PRESENTATION: Record<string, CabinPresentation> = {
  // ── 内容生产:5 内容舱(dept 主角图) ──
  ai_explosive_parse: { render: "dept", icon: Flame, hub: "explosive" },
  ai_poster: { render: "dept", icon: ImageIcon, hub: "poster" },
  ai_video: { render: "dept", icon: Film, hub: "video" },
  ai_digital_human: { render: "dept", icon: Clapperboard, hub: "dh" },
  // 官媒舱 = AI记者:R2-T2 转正(R1 后端已通,catalog V14 翻 active),dept 富卡。
  ai_reporter: { render: "dept", icon: Newspaper, hub: "reporter" },
  // 漫剧工坊(2026-09-01 即将卡 → 2026-09-15 老板拍板上线):独立站,点击新开页跳
  // drama.xbwhynot.com(catalog V23 翻 active/gated=0/billable=0;账号与计费都在 drama
  // 站内,本站不设门)。
  //
  // ★2026-09-20 老板:「漫剧工坊的卡片风格还没有跟其他的统一,希望能突出一些,因为毕竟是个大功能」。
  //   改法 = render 从 capability 升 external:内容群里另外 5 张是 dept 富卡,它此前是轻量卡,
  //   同一组里一眼就能看出「这是另一种卡」。现在与 dept 卡共用 `WorkbenchCabinShell`
  //   (同高 / 同圆角 / 同主角图处理 / 同 hover),但**不进 DEPT_ENTRIES**:那张表的每行都要
  //   hub + route + 积分标,外链舱三样都没有,塞假条目是骗组件。
  // ★「突出」只用体系内已有的手段,不新增渐变/霓虹/夸张动效:
  //   ① chart=1 是 5 张 dept 卡(2/3/4/5/6)唯一没占的身份色档 —— 它有了自己的身份色;
  //   ② 富卡外壳的 `feature` 档把**已有的** .wb-accent 条(渐隐 3px → 实色 4px)与 .wb-mesh
  //      (0.12 → 0.20)各加深一档,同组里只此一张。
  // ★诚实:externalUrl 在,卡上带「独立站 ↗」标 + 「前往独立站」CTA + aria 含「在新标签页打开」;
  //   noteKey 占的是积分标的位子,说的是「为什么这张卡没有价」,而**不是**编一个本站价。
  // chips 三条逐字来自后端 catalog 那句简介(V4:「AI 生成角色与分镜,配音成片」),不自造能力。
  // 主角图 cap-ai_comic_drama.webp ×3 皮肤已在 public/scene-figures/ 下(注释曾长期写「待产出」,过期了)。
  ai_comic_drama: {
    render: "external",
    icon: Drama,
    externalUrl: "https://drama.xbwhynot.com",
    // OEM:外链独立站随品牌开关(渠道域名默认关,直营开)。
    brandModule: "comicDrama",
    ctaVerb: "enter",
    chart: 1,
    chipKeys: [
      "cabin.chip.comicDrama.role",
      "cabin.chip.comicDrama.storyboard",
      "cabin.chip.comicDrama.dub",
    ],
    noteKey: "cabin.note.comicDrama",
    sourceLabelKey: "cabin.source.comicDrama",
    kindKey: "cabin.kind.comicDrama",
  },

  // ── 获客推广 ──
  geo_radar: {
    render: "capability",
    icon: Radar,
    route: "/geo-radar",
    ctaVerb: "enter",
    chipKeys: ["cabin.chip.geo.compete", "cabin.chip.geo.source"],
    sourceLabelKey: "cabin.source.geo",
    kindKey: "cabin.kind.geo",
  },
  // kindKey(左下角那枚胶囊)2026-07-31 补:改之前只有已开舱有标签,即将舱那格是空的 ——
  // 同一面墙上一半卡有「这是个什么 AI」一半没有。文案与 sourceLabelKey 同字不同位:
  // kind 在图上(扫墙时看到),source 在卡脚(读细节时看到)。
  // 2026-08-23 转正:地图获客(自研)+ 外呼/邮件/数据获客(GEO-CARDS 外链迁入)四子能力齐了,
  // 2026-08-26 第四张卡改名 GEO获客 → 数据获客(只改名,外链与反代前缀 yg400 原样)。
  // 2026-09-21 数据获客的外链改成跟外呼获客同一个(xiaobang),见 ExternalToolsPage 的 XIAOBANG_LINK。
  // 后端目录 V9 已 coming_soon→active;route 落舱 hub。
  // 拓客工作台(1028):启客拓客整页嵌进获客舱。gated=0(不是套餐模块),
  // 可见性由 useClusterCards 按 /acq/qike/status 判 —— 平台没配 QIKE_* 就不上墙。
  qike_workbench: {
    render: "capability",
    icon: Building2,
    route: "/ai-huoke/qike",
    ctaVerb: "enter",
    chipKeys: ["cabin.chip.qike.search", "cabin.chip.qike.unlock"],
    sourceLabelKey: "cabin.source.qike",
    kindKey: "cabin.kind.qike",
  },
  ai_acquisition: {
    render: "capability",
    icon: MapPin,
    route: "/ai-huoke",
    ctaVerb: "enter",
    chipKeys: ["cabin.chip.huoke.map", "cabin.chip.huoke.channels"],
    sourceLabelKey: "cabin.source.acquisition",
    kindKey: "cabin.kind.acquisition",
  },
  // 建站舱(2026-09-22 转正):AI 官网 —— 知识库生成内容 / 官网 AI 客服接待 / 自有域名。
  // route 落舱 hub「我的站点」;站点级页面(采集 / 编辑 / 域名)带 :id,见 App.tsx 路由块。
  // 有真 module 门(ai_website),卡的可见性走 useClusterCards 的 gate,不写死角色。
  ai_website: {
    render: "capability",
    icon: Globe,
    route: "/ai-website",
    ctaVerb: "enter",
    chipKeys: ["cabin.chip.website.kb", "cabin.chip.website.chat", "cabin.chip.website.domain"],
    sourceLabelKey: "cabin.source.website",
    kindKey: "cabin.kind.website",
  },
  // ── 获客推广 · 推广舱/官媒舱(2026-08-09 起走后端 catalog) ──
  // 曾是两张前端写死的过渡「即将」卡(LOCAL_SOON_CARDS);现在 ai_promotion 挂了
  // cluster、ai_official_media 落了库,卡由后端下发,这里只剩纯画法(icon / 文案 key)。
  // 1012 过渡软链:后端仍 coming_soon,品牌开了 geoAdmin 时前端当已开卡,route 落 GEO 管理系统(PromotionGeoPage)。
  ai_promotion: {
    render: "capability",
    icon: Megaphone,
    route: PROMOTION_SOFTLINK_ROUTE,
    ctaVerb: "enter",
    sourceLabelKey: "cabin.source.geo",
    kindKey: "cabin.kind.promotion",
  },
  ai_official_media: {
    render: "capability",
    icon: Radio,
    // 显示阶段转正(2026-09-15):媒体采购网资源只读检索页;投稿暂缓。卡墙直进 /ai-official-media。
    route: "/ai-official-media",
    ctaVerb: "enter",
    sourceLabelKey: "cabin.source.reporter",
    kindKey: "cabin.kind.officialMedia",
  },
  // 短信获客(2026-09-01 老板拍板即将卡,catalog V4 落库 coming_soon)。
  // 主角图 cap-ai_sms.webp ×3 皮肤 2026-09-19 已补齐(与推广舱/官媒舱/算力中心同批)。
  ai_sms: {
    render: "capability",
    icon: MessageSquareText,
    sourceLabelKey: "cabin.source.sms",
    kindKey: "cabin.kind.sms",
  },

  // ── 成交服务 ──
  ai_guide: {
    render: "capability",
    icon: MessagesSquare,
    sourceLabelKey: "cabin.source.guide",
  },
  ai_kefu: {
    render: "capability",
    icon: Headphones,
    route: "/ai-kefu",
    ctaVerb: "enter",
    chipKeys: ["cabin.chip.kefu.kb", "cabin.chip.kefu.handover"],
    sourceLabelKey: "cabin.source.kefu",
    kindKey: "cabin.kind.kefu",
  },
  ai_peilian: {
    render: "capability",
    icon: GraduationCap,
    route: "/ai-peilian",
    ctaVerb: "enter",
    chipKeys: ["cabin.chip.peilian.spar", "cabin.chip.peilian.score"],
    sourceLabelKey: "cabin.source.peilian",
    kindKey: "cabin.kind.peilian",
  },

  // ── 情报决策 ──
  // V25 军师回归:两张真卡排在占位舱之前(后端 listClustered 按 id 序,biz_advisor=5 / advisor_board=7)。
  // ai_business「商业舱」已退役让位(简介与经营诊断讲同一件事),catalog cluster 置 NULL,故此处无 overlay。
  biz_advisor: {
    render: "capability",
    icon: Compass,
    route: "/biz-advisor",
    ctaVerb: "enter",
    chipKeys: ["cabin.chip.bizAdvisor.diagnose", "cabin.chip.bizAdvisor.compete"],
    sourceLabelKey: "cabin.source.bizAdvisor",
    kindKey: "cabin.kind.bizAdvisor",
    // 角色不写死:biz_advisor 有真 module 门(RbacMatrix 不给 STAFF),由 useModuleVisible 判。
  },
  // 主角图:两张新卡都要 `cap-<capability>.webp` × 三皮肤,缺了会**静默**退成 icon 秃卡
  // (消费端用 background-image,404 不报错)。经营诊断继承退役的 ai_business 那张(商务人 + 上升
  // 折线,正是「经营体检」的语义);经营看板用通用备用小人 `_spare_welcome` —— 别的卡画的是
  // 「某个员工在干活」,这张画的是**小阳本人**把数据聚成一屏,出镜说得通。想更贴题可另产一张。
  advisor_board: {
    render: "capability",
    icon: Gauge,
    // 2026-07-30 四合一:直接落经营看板页(看盘 · 对话记录 · 记忆 · 技能 · 记忆审计 一页收口)。
    // 角色分流下沉进页内 `boardVariantFor`(ADMIN 驾驶舱 / MANAGER 团队看板),presentation 仍是
    // 静态数据、不长角色逻辑 —— 但落点得是那一页,不是一个只会把人弹去老驾驶舱的中转页。
    route: "/advisor",
    ctaVerb: "enter",
    chipKeys: ["cabin.chip.advisorBoard.output", "cabin.chip.advisorBoard.rank"],
    sourceLabelKey: "cabin.source.advisorBoard",
    kindKey: "cabin.kind.advisorBoard",
    // gated=0 无 entitlement 可判 → 只能按角色:STAFF 看不到(团队看板/驾驶舱都是 MANAGER+)。
    roles: [ROLES.TENANT_ADMIN, ROLES.TENANT_MANAGER],
  },
  ai_research: {
    render: "capability",
    icon: BarChart3,
    sourceLabelKey: "cabin.source.research",
  },
  ai_monitor: {
    render: "capability",
    icon: Eye,
    sourceLabelKey: "cabin.source.monitor",
  },

  // ── 运营 ──
  media_matrix: {
    render: "capability",
    icon: Radio,
    sourceLabelKey: "cabin.source.mediaMatrix",
  },
  // 算力中心(2026-09-01 即将卡,归运营群;meta 型舱 billable=0,不进套餐可售清单)。
  // ★1007 用户反馈「算力平台也做成漫剧工坊一样,直接开 token.xbwhynot.com」:照漫剧工坊升 external
  //   富卡、点击新开页跳我方 new-api 算力站(API 令牌 / 充值 / 用量看板,账号与计费都在算力站内,
  //   本站不设门不计费)。catalog 由 api V11 翻 active;后端翻回 coming_soon 时自动退回轻量占位卡。
  // chips 三条只写算力站真有的东西;OEM 随品牌开关 computeCenter(渠道默认关,直营开)。
  compute_center: {
    render: "external",
    icon: Cpu,
    externalUrl: "https://token.xbwhynot.com",
    brandModule: "computeCenter",
    ctaVerb: "enter",
    // 身份色与漫剧同档(chart=1 是 dept 卡唯一没占的档);两张外链卡分属内容 / 运营两群,不同墙。
    chart: 1,
    chipKeys: [
      "cabin.chip.computeCenter.token",
      "cabin.chip.computeCenter.recharge",
      "cabin.chip.computeCenter.usage",
    ],
    noteKey: "cabin.note.computeCenter",
    sourceLabelKey: "cabin.source.computeCenter",
    kindKey: "cabin.kind.computeCenter",
  },
};

/** 取舱画法;未知 capability 返回 undefined(诚实,不吞后端新值)。 */
export function presentationOf(capability: string): CabinPresentation | undefined {
  return CABIN_PRESENTATION[capability];
}

/**
 * OEM 贴牌(§2.4):这张舱卡是否被当前品牌的模块开关藏掉。
 * 未挂 brandModule 的舱永远不藏;未知 capability 不藏(由 presentationOf 的 undefined 另行处理)。
 */
export function isCapabilityHiddenByBrand(
  capability: string,
  brand: Pick<Brand, "modules"> = getBrand()
): boolean {
  const mod = CABIN_PRESENTATION[capability]?.brandModule;
  return !!mod && !brand.modules[mod];
}

/**
 * 外链舱的统一开法:新标签页 + `noopener,noreferrer`。
 *
 * ★为什么收成一个函数:首页横滑带(Index)与群页卡墙(ClusterPage)原来各写了一遍
 * `window.open(url, "_blank", "noopener,noreferrer")`,注释还自嘲「口径与群页逐字相同」——
 * 逐字相同的两份就是双源,0918 那次「首页点漫剧不跳」正是两处没对齐(首页那份只认 route)。
 * `noopener` 不是可选项:新页拿到 window.opener 就能改写本站地址(反向 tabnabbing)。
 */
export function openExternalCabin(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}
